import { Escape } from './escape.xml';
import { exportVNodeToString } from './export.string';

export type VNodeChildren = (VNode | string)[];
export type VNodeAttrs = { [key: string]: any };

export interface VNodeExportOptions {
	lowerCaseTagName?: boolean;
	indent?: string;
	ignoreKeys?: string[];
	keyMap?: { [key: string]: string };
}

export interface VNode {
	attrs: VNodeAttrs;
	children: VNode[];
	tag: string;
	text?: string;
}

export interface VNodeText extends VNode {
	tag: '#';
	attrs: undefined;
	children: undefined;
	text: string;
}

export interface VNodeTrustedText extends VNode {
	tag: '<';
	attrs: undefined;
	children: undefined;
	text: string;
}

export const TRUSTED_TEXT_TAG = '<';
export const TEXT_TAG = '#';
export const FRAGMENT_TAG = '[';

export function normaliseNodes(children: VNodeChildren): VNode[] {
	if (Array.isArray(children)) {
		for (var i = 0; i < children.length; i++) {
			var child = children[i];
			if (typeof child === 'string') {
				children[i] = IkeXML.text(child);
			}
		}
	}
	return children as VNode[];
}

const EMPTY_ATTRS = Object.freeze({});
const EMPTY_ARRAY = <VNode[]>Object.freeze([]);

function n(tag: string): VNode;
function n(tag: string, text: string): VNode;
function n(tag: string, child: VNode): VNode;
function n(tag: string, children: VNodeChildren): VNode;
function n(tag: string, attrs: VNodeAttrs): VNode;
function n(tag: string, attrs: VNodeAttrs, text: string): VNode;
function n(tag: string, attrs: VNodeAttrs, child: VNode): VNode;
function n(tag: string, attrs: VNodeAttrs, children: VNodeChildren): VNode;
function n(
	tag: string,
	attrs?: VNodeAttrs | VNodeChildren | VNode | string,
	children?: VNodeChildren | VNode | string
): VNode {
	var actualAttrs: VNodeAttrs;
	var actualChildren: VNodeChildren;

	if (IkeXML.isVNodeChildren(attrs)) {
		actualChildren = normaliseNodes(attrs);
	} else if (IkeXML.isNode(attrs)) {
		actualChildren = [attrs];
	} else if (IkeXML.isVNodeAttrs(attrs)) {
		actualAttrs = attrs;
	} else if (typeof attrs === 'string') {
		actualChildren = [IkeXML.text(attrs)];
	}

	// If a child was passed in was it wrapped in a array or not?
	if (children != null) {
		if (IkeXML.isNode(children)) {
			actualChildren = [children];
		} else if (typeof children === 'string') {
			actualChildren = [IkeXML.text(children)];
		} else {
			actualChildren = normaliseNodes(children);
		}
	}

	return {
		tag,
		attrs: actualAttrs || {},
		children: (actualChildren || []) as VNode[],
	};
}

const isVNodeChildren = (k: unknown): k is VNodeChildren => Array.isArray(k);
const isVNodeAttrs = (k: unknown): k is VNodeAttrs => typeof k === 'object';
export const IkeXML = {
	Escape,
	Export: {
		asString: exportVNodeToString,
	},
	EMPTY_ATTRS,
	EMPTY_ARRAY,

	n,
	isVNodeChildren,
	isVNodeAttrs,

	text(text: string): VNode {
		return {
			tag: TEXT_TAG,
			attrs: EMPTY_ATTRS,
			children: EMPTY_ARRAY,
			text,
		};
	},

	trust(text: string): VNode {
		return {
			tag: TRUSTED_TEXT_TAG,
			attrs: EMPTY_ATTRS,
			children: EMPTY_ARRAY,
			text,
		};
	},

	fragment(...nodes: VNode[]): VNode {
		return {
			tag: FRAGMENT_TAG,
			attrs: EMPTY_ATTRS,
			children: nodes,
		};
	},

	isNode(node: unknown): node is VNode {
		return node != null && (node as VNode).tag != null;
	},

	isTrustedTextNode(node: unknown): node is VNodeTrustedText {
		return node != null && (node as VNode).tag === TRUSTED_TEXT_TAG;
	},

	isTextNode(node: unknown): node is VNodeText {
		return node != null && (node as VNode).tag === TEXT_TAG;
	},

	isFragmentNode(node: unknown): node is VNode {
		return node != null && (node as VNode).tag === FRAGMENT_TAG;
	},
};
